import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import Fieldset from "@/components/Fieldset";
import Field from "@/components/Field";
import { isRequired } from "@/lib/form";
import SettlementTypeSelect from "@/modules/projects/components/SettlementTypeSelect";
import { RoleBasedProjectSettlementTypeService } from "@/modules/projects/service/settlementType";
import StoreSelect from "@/components/StoreSelect";
import AsyncButton from "@/components/AsyncButton";
import { PenLine } from "lucide-react";
import DatePicker from "@/components/DatePicker";
import { NumberInput } from "@/components/ui/NumberInput";
import ProjectRoleSelect from "@/modules/projects/components/ProjectRoleSelect";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SETTLEMENT_TYPE_HOURLY_RATE } from "@/schemas/commonFields/settlementType";
import {
  ReadRoleBasedProjectRates,
  writeRoleBasedProjectRatesBase,
} from "@/schemas/roleBasedProjectRates";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useDefaultRates } from "@/modules/defaultRates/hooks/useDefaultRates";
import { useFormContext } from "react-hook-form";
import { WriteRoleBasedProjectRates } from "@/schemas/roleBasedProjectRates";
import { useProjectQuery } from "@/modules/projects/hooks/useProjectQuery";

interface ProjectRateFormFieldsProps {
  isPending: boolean;
  projectRate?: ReadRoleBasedProjectRates;
}

function ProjectRateFormFields({
  isPending,
  projectRate,
}: ProjectRateFormFieldsProps) {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const projectQuery = useProjectQuery(projectId);
  const form = useFormContext<WriteRoleBasedProjectRates>();
  const settlementType = form.watch("settlement_type");
  const validFrom = form.watch("valid_from");

  const isHourlyRate = settlementType === SETTLEMENT_TYPE_HOURLY_RATE;

  const {
    applyDefaultRates,
    defaultRatesValidFromDate,
    isLoading: defaultRatesLoading,
  } = useDefaultRates({
    form,
    settlementType,
    validFrom,
    enabled: isHourlyRate,
  });
  const project = projectQuery.data;
  const projectStartDate = project?.start_date;
  const projectEndDate = project?.end_date;
  const projectRoleName = projectRate?.project_role_name;
  const storeName = projectRate?.store_name;
  const isButtonDisabled =
    isPending || !settlementType || defaultRatesLoading || !isHourlyRate;

  const tooltipMessage = useMemo(() => {
    if (settlementType && !isHourlyRate) {
      return t("projectRates.buttons.tooltipOnlyHourlyRate");
    }
    if (!settlementType) {
      return t("projectRates.buttons.tooltipMissingFields", {
        fields: t("projectRates.fields.settlement_type"),
      });
    }
    return null;
  }, [settlementType, isHourlyRate, t]);

  return (
    <>
      <Fieldset className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <div className="flex flex-row gap-4">
          <Field
            name="settlement_type"
            label={t(`projectRates.fields.settlement_type`)}
            required={isRequired(
              writeRoleBasedProjectRatesBase.shape.settlement_type,
            )}
            wrapperClassName="grow"
            className="space-y-2"
            render={({ field }) => (
              <SettlementTypeSelect
                className="w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                settlementTypeService={RoleBasedProjectSettlementTypeService}
                loading="eager"
                {...field}
              />
            )}
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block max-w-full self-end w-fit flex-shrink-0 min-w-[280px]">
                  <AsyncButton
                    className="w-full"
                    labelClassName="overflow-hidden text-ellipsis whitespace-nowrap"
                    type="button"
                    disabled={isButtonDisabled}
                    loadingLabel={t(`projectRates.buttons.loadingDefaultRates`)}
                    label={t(`projectRates.buttons.applyDefaultRates`)}
                    loading={defaultRatesLoading}
                    icon={<PenLine className="w-6 h-6 mr-3" />}
                    onClick={applyDefaultRates}
                  />
                </span>
              </TooltipTrigger>
              {tooltipMessage && (
                <TooltipContent>{tooltipMessage}</TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
        <Field
          name="project_role_id"
          label={t(`projectRates.fields.project_role_name`)}
          required={isRequired(
            writeRoleBasedProjectRatesBase.shape.project_role_id,
          )}
          className="space-y-2"
          render={({ field }) => (
            <ProjectRoleSelect
              projectId={projectId}
              className="w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              loading="eager"
              loadingCondition={Boolean(projectId)}
              {...field}
              defaultItem={{ id: field.value, name: projectRoleName }}
            />
          )}
        />
      </Fieldset>

      <div className="text-sm mt-1 text-green-600 h-10">
        {defaultRatesValidFromDate &&
          t(`projectRates.defaultRatesFromDate`, {
            date: defaultRatesValidFromDate,
          })}
      </div>

      <Fieldset className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Field
          name="rate_student_net"
          label={t(`projectRates.fields.rate_student_net`)}
          required={isRequired(
            writeRoleBasedProjectRatesBase.shape.rate_student_net,
          )}
          render={({ field }) => <NumberInput {...field} />}
        />
        <Field
          name="rate_student_tax"
          label={t(`projectRates.fields.rate_student_tax`)}
          required={isRequired(
            writeRoleBasedProjectRatesBase.shape.rate_student_tax,
          )}
          render={({ field }) => <NumberInput {...field} />}
        />
        <Field
          name="rate_kch_net"
          label={t(`projectRates.fields.rate_kch_net`)}
          required={isRequired(
            writeRoleBasedProjectRatesBase.shape.rate_kch_net,
          )}
          render={({ field }) => <NumberInput {...field} />}
        />
      </Fieldset>

      <Fieldset className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Field
          name="rate_kch_tax"
          label={t(`projectRates.fields.rate_kch_tax`)}
          required={isRequired(
            writeRoleBasedProjectRatesBase.shape.rate_kch_tax,
          )}
          render={({ field }) => <NumberInput {...field} />}
        />
        <Field
          name="rate_zus_net"
          label={t(`projectRates.fields.rate_zus_net`)}
          required={isRequired(
            writeRoleBasedProjectRatesBase.shape.rate_zus_net,
          )}
          render={({ field }) => <NumberInput {...field} />}
        />
        <Field
          name="rate_zus_tax"
          label={t(`projectRates.fields.rate_zus_tax`)}
          required={isRequired(
            writeRoleBasedProjectRatesBase.shape.rate_zus_tax,
          )}
          render={({ field }) => <NumberInput {...field} />}
        />
      </Fieldset>

      <Fieldset className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Field
          name="rate_zus_sick_net"
          label={t(`projectRates.fields.rate_zus_sick_net`)}
          required={isRequired(
            writeRoleBasedProjectRatesBase.shape.rate_zus_sick_net,
          )}
          render={({ field }) => <NumberInput {...field} />}
        />
        <Field
          name="rate_zus_sick_tax"
          label={t(`projectRates.fields.rate_zus_sick_tax`)}
          required={isRequired(
            writeRoleBasedProjectRatesBase.shape.rate_zus_sick_tax,
          )}
          render={({ field }) => <NumberInput {...field} />}
        />
        <Field
          name="rate_gross"
          label={t(`projectRates.fields.rate_gross`)}
          required={isRequired(writeRoleBasedProjectRatesBase.shape.rate_gross)}
          render={({ field }) => <NumberInput {...field} />}
        />
      </Fieldset>

      <Fieldset className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Field
          name="store_id"
          label={t(`projectRates.fields.store_id`)}
          required={isRequired(writeRoleBasedProjectRatesBase.shape.store_id)}
          render={({ field }) => (
            <StoreSelect
              className="w-full"
              loading="eager"
              {...field}
              defaultItem={{ id: field.value, name: storeName }}
            />
          )}
        />
        <Field
          name="valid_from"
          label={t(`projectRates.fields.valid_from`)}
          required={isRequired(writeRoleBasedProjectRatesBase.shape.valid_from)}
          render={({ field }) => (
            <DatePicker
              className="w-full"
              {...field}
              value={field.value || projectStartDate || undefined}
              disabled={
                !projectStartDate || !projectEndDate || form.formState.disabled
              }
              datePickerProps={
                projectStartDate && projectEndDate
                  ? {
                      startMonth: new Date(projectStartDate),
                      endMonth: new Date(projectEndDate),
                      disabled: {
                        before: new Date(projectStartDate),
                        after: new Date(projectEndDate),
                      },
                    }
                  : undefined
              }
            />
          )}
        />
      </Fieldset>

      <Fieldset className="grid grid-cols-1 gap-4">
        <Field
          name="description"
          label={t(`projectRates.fields.description`)}
          render={({ field }) => <Textarea {...field} className="h-24" />}
        />
      </Fieldset>
    </>
  );
}

export default ProjectRateFormFields;
